// ──────────────────────────────────────────────────────────────────────
// Batch 2B C12 (BUG-RW-CAL-012 + BUG-RW-GT-009) — User Settings page.
// Sections: Profile, Appearance, Notifications, Workspace (Owner/Admin
// shortcut). URL hashes route to each section: /settings#profile,
// #appearance, #notifications, #workspace. Theme toggle here mirrors the
// avatar dropdown toggle; both write to user preferences via
// authAPI.patchPreferences AND ThemeContext so the choice persists
// locally + on the server.
// ──────────────────────────────────────────────────────────────────────
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, User, Palette, Bell, Building2, Sun, Moon, Globe, Check, ShieldCheck, FileText, Mail } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import ProfileAvatarSection from '../components/ProfileAvatarSection';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import { useTheme } from '../contexts/ThemeContext';
import { authAPI } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { useWorkspace } from '../contexts/WorkspaceContext';
import BrandTextRulesPanel from '../components/BrandTextRulesPanel';
import TaskTemplatesPanel from '../components/TaskTemplatesPanel';
import EmailHealthCard from '../components/EmailHealthCard';

const TIMEZONES = [
  'America/Regina', 'America/Toronto', 'America/Vancouver', 'America/New_York',
  'America/Chicago', 'America/Denver', 'America/Los_Angeles', 'UTC',
  'Europe/London', 'Europe/Paris', 'Asia/Kolkata', 'Asia/Dubai', 'Asia/Singapore',
  'Asia/Tokyo', 'Australia/Sydney',
];

const NOTIFICATION_EVENTS = [
  { key: 'task_assigned',     label: 'Task assigned to me' },
  { key: 'mention',           label: 'I am mentioned in a comment or chat' },
  { key: 'comment',           label: 'New comment on my task' },
  { key: 'deadline',          label: 'Deadline approaching (24h before)' },
  { key: 'submission_review', label: 'A submission needs my review' },
];

const SECTIONS = [
  { id: 'profile',       label: 'Profile',       icon: User,      roles: 'all' },
  { id: 'appearance',    label: 'Appearance',    icon: Palette,   roles: 'all' },
  { id: 'notifications', label: 'Notifications', icon: Bell,      roles: 'all' },
  { id: 'workspace',     label: 'Workspace',     icon: Building2, roles: 'admin' },
  { id: 'brand-rules',   label: 'Brand Rules',   icon: ShieldCheck, roles: 'admin' },
  { id: 'task-templates', label: 'Task Templates', icon: FileText, roles: 'admin' },
  // D64 — Owner-only Email Health diagnostics.
  { id: 'email',         label: 'Email',         icon: Mail,      roles: 'owner' },
];

export default function UserSettingsPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, refreshUser } = useAuth();
  const { workspaceRole, currentWorkspace } = useWorkspace();
  const themeCtx = useTheme();
  // RF-A1 — use workspace-scoped role from WorkspaceContext, not the user
  // object (which doesn't carry workspace_role from /auth/me). Owner ALWAYS
  // sees Workspace section.
  const isAdminLike = ['owner', 'admin'].includes(workspaceRole || user?.role);
  const isOwner = (workspaceRole || user?.role) === 'owner' || (workspaceRole || user?.role) === 'super_admin';

  // Hash-routed section
  const hash = (location.hash || '#profile').replace('#', '');
  const activeSection = SECTIONS.some(s => s.id === hash) ? hash : 'profile';

  // Profile state
  const [name, setName] = useState(user?.name || '');
  const [timezone, setTimezone] = useState(user?.timezone || 'America/Regina');
  const [savingProfile, setSavingProfile] = useState(false);

  // Notification toggle state
  const initialNotifs = useMemo(() => {
    const seed = {};
    NOTIFICATION_EVENTS.forEach(ev => {
      const stored = user?.preferences?.notifications?.[ev.key];
      seed[`${ev.key}_email`] = stored?.email ?? true;
      seed[`${ev.key}_inapp`] = stored?.inapp ?? true;
    });
    return seed;
  }, [user]);
  const [notifs, setNotifs] = useState(initialNotifs);
  const [savingNotifs, setSavingNotifs] = useState(false);

  useEffect(() => { setName(user?.name || ''); setTimezone(user?.timezone || 'America/Regina'); }, [user]);

  const handleSaveProfile = async () => {
    setSavingProfile(true);
    try {
      await authAPI.updateProfile({ name: name.trim(), timezone });
      toast.success('Profile saved');
      refreshUser && refreshUser();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Save failed');
    } finally {
      setSavingProfile(false);
    }
  };

  const handleSaveNotifs = async () => {
    setSavingNotifs(true);
    try {
      const grouped = {};
      NOTIFICATION_EVENTS.forEach(ev => {
        grouped[ev.key] = { email: !!notifs[`${ev.key}_email`], inapp: !!notifs[`${ev.key}_inapp`] };
      });
      await authAPI.patchPreferences({ notifications: grouped });
      toast.success('Notification preferences saved');
      refreshUser && refreshUser();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Save failed');
    } finally {
      setSavingNotifs(false);
    }
  };

  const setSection = (id) => navigate(`/settings#${id}`, { replace: false });

  return (
    <div className="max-w-5xl mx-auto p-4 md:p-8" data-testid="settings-page">
      <div className="flex items-center gap-3 mb-6">
        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => navigate(-1)} data-testid="settings-back-btn" aria-label="Back">
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <h1 className="font-display text-[26px] font-semibold text-foreground">Settings</h1>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-[200px_1fr] gap-6">
        <nav className="space-y-1" data-testid="settings-nav">
          {SECTIONS.filter(s => s.roles === 'all' || (s.roles === 'admin' && isAdminLike) || (s.roles === 'owner' && isOwner)).map(s => {
            const Icon = s.icon;
            const active = activeSection === s.id;
            return (
              <button
                key={s.id}
                onClick={() => setSection(s.id)}
                className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-[13px] font-medium ${active ? 'bg-gold/15 text-gold' : 'text-foreground/60 hover:bg-foreground/[0.04]'}`}
                data-testid={`settings-nav-${s.id}`}
              >
                <Icon className="h-4 w-4" />
                {s.label}
              </button>
            );
          })}
        </nav>

        <div>
          {activeSection === 'profile' && (
            <Card className="bg-card border-border/30" data-testid="settings-section-profile">
              <CardContent className="p-6 space-y-4">
                <h2 className="font-display text-[18px] font-semibold text-foreground">Profile</h2>
                <ProfileAvatarSection
                  user={user}
                  onChanged={(newUrl) => {
                    // Optimistically refresh `user.avatar_url` so sidebar
                    // + chips pick up the new photo without a full reload.
                    try {
                      const evt = new CustomEvent('stellar-avatar-updated', { detail: { avatar_url: newUrl } });
                      window.dispatchEvent(evt);
                    } catch { /* noop */ }
                  }}
                />
                <div className="space-y-1.5">
                  <Label htmlFor="profile-name" className="text-[12px]">Display Name</Label>
                  <Input id="profile-name" value={name} onChange={e => setName(e.target.value)} className="h-9 text-[13px] bg-foreground/[0.02] border-border/30" data-testid="settings-profile-name" />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="profile-email" className="text-[12px]">Email</Label>
                  <Input id="profile-email" value={user?.email || ''} disabled className="h-9 text-[13px] bg-foreground/[0.02] border-border/30 opacity-70" data-testid="settings-profile-email" />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="profile-timezone" className="text-[12px] flex items-center gap-1"><Globe className="h-3 w-3" /> Timezone</Label>
                  <select
                    id="profile-timezone"
                    value={timezone}
                    onChange={e => setTimezone(e.target.value)}
                    className="w-full h-9 text-[13px] bg-foreground/[0.02] border border-border/30 rounded-md px-2 outline-none"
                    data-testid="settings-profile-timezone"
                  >
                    {TIMEZONES.map(tz => <option key={tz} value={tz}>{tz}</option>)}
                  </select>
                  <p className="text-[11px] text-muted-foreground/40">Calendar, deadlines, and email digests use this timezone.</p>
                </div>
                <Button onClick={handleSaveProfile} disabled={savingProfile} className="bg-gold/10 hover:bg-gold/20 text-gold border-0" data-testid="settings-profile-save">
                  {savingProfile ? 'Saving...' : 'Save Profile'}
                </Button>
              </CardContent>
            </Card>
          )}

          {activeSection === 'appearance' && (
            <Card className="bg-card border-border/30" data-testid="settings-section-appearance">
              <CardContent className="p-6 space-y-4">
                <h2 className="font-display text-[18px] font-semibold text-foreground">Appearance</h2>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-[14px] font-medium text-foreground">Theme</p>
                    <p className="text-[12px] text-muted-foreground/50">Switch between dark and light modes. Choice persists across devices.</p>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      onClick={() => { themeCtx?.setTheme && themeCtx.setTheme('dark'); authAPI.patchPreferences({ theme: 'dark' }).catch(() => {}); }}
                      variant={themeCtx?.theme === 'dark' ? 'default' : 'outline'}
                      size="sm"
                      className={themeCtx?.theme === 'dark' ? 'bg-gold/15 text-gold border-0' : 'border-border/30'}
                      data-testid="settings-theme-dark"
                    ><Moon className="h-3 w-3 mr-1" /> Dark{themeCtx?.theme === 'dark' && <Check className="h-3 w-3 ml-1" />}</Button>
                    <Button
                      onClick={() => { themeCtx?.setTheme && themeCtx.setTheme('light'); authAPI.patchPreferences({ theme: 'light' }).catch(() => {}); }}
                      variant={themeCtx?.theme === 'light' ? 'default' : 'outline'}
                      size="sm"
                      className={themeCtx?.theme === 'light' ? 'bg-gold/15 text-gold border-0' : 'border-border/30'}
                      data-testid="settings-theme-light"
                    ><Sun className="h-3 w-3 mr-1" /> Light{themeCtx?.theme === 'light' && <Check className="h-3 w-3 ml-1" />}</Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {activeSection === 'notifications' && (
            <Card className="bg-card border-border/30" data-testid="settings-section-notifications">
              <CardContent className="p-6 space-y-4">
                <h2 className="font-display text-[18px] font-semibold text-foreground">Notifications</h2>
                <p className="text-[12px] text-muted-foreground/50">Choose how you want to be notified for each event type.</p>
                <table className="w-full text-[13px]">
                  <thead>
                    <tr className="text-left text-[11px] text-muted-foreground/60">
                      <th className="pb-2">Event</th>
                      <th className="pb-2 w-20 text-center">Email</th>
                      <th className="pb-2 w-20 text-center">In-app</th>
                    </tr>
                  </thead>
                  <tbody>
                    {NOTIFICATION_EVENTS.map(ev => (
                      <tr key={ev.key} className="border-t border-border/10" data-testid={`notif-row-${ev.key}`}>
                        <td className="py-2 text-foreground/80">{ev.label}</td>
                        <td className="py-2 text-center"><Switch checked={!!notifs[`${ev.key}_email`]} onCheckedChange={v => setNotifs(p => ({ ...p, [`${ev.key}_email`]: v }))} data-testid={`notif-toggle-${ev.key}-email`} /></td>
                        <td className="py-2 text-center"><Switch checked={!!notifs[`${ev.key}_inapp`]} onCheckedChange={v => setNotifs(p => ({ ...p, [`${ev.key}_inapp`]: v }))} data-testid={`notif-toggle-${ev.key}-inapp`} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <Button onClick={handleSaveNotifs} disabled={savingNotifs} className="bg-gold/10 hover:bg-gold/20 text-gold border-0" data-testid="settings-notif-save">
                  {savingNotifs ? 'Saving...' : 'Save Preferences'}
                </Button>
              </CardContent>
            </Card>
          )}

          {activeSection === 'workspace' && isAdminLike && (
            <Card className="bg-card border-border/30" data-testid="settings-section-workspace">
              <CardContent className="p-6 space-y-4">
                <h2 className="font-display text-[18px] font-semibold text-foreground">Workspace</h2>
                <p className="text-[12px] text-muted-foreground/50">Workspace name, members, org chart, and admin tools live on the workspace admin page.</p>
                <Button variant="outline" className="border-border/30" onClick={() => navigate('/tasks/settings')} data-testid="settings-workspace-admin">
                  Open Workspace Admin →
                </Button>
                <div className="border-t border-border/10 pt-3 mt-3">
                  <p className="text-[12px] text-muted-foreground/40">Billing summary coming soon.</p>
                </div>
              </CardContent>
            </Card>
          )}

          {/* D52a Part C — Brand Rules accessible from sidebar Settings */}
          {activeSection === 'brand-rules' && isAdminLike && (
            <div data-testid="settings-section-brand-rules">
              <BrandTextRulesPanel workspaceId={currentWorkspace?.id} />
            </div>
          )}

          {/* D52b Part D — Task Templates manager */}
          {activeSection === 'task-templates' && isAdminLike && (
            <div data-testid="settings-section-task-templates">
              <TaskTemplatesPanel workspaceId={currentWorkspace?.id} />
            </div>
          )}

          {/* D64 — Email Health (Owner-only) */}
          {activeSection === 'email' && isOwner && (
            <div data-testid="settings-section-email">
              <EmailHealthCard defaultTestEmail={user?.email || ''} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
